import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const NAV_ITEMS = [
  { id: "about", key: "about" },
  { id: "musicians", key: "musicians" },
  { id: "discography", key: "discography" },
  { id: "concerts", key: "concerts" },
  { id: "gallery", key: "gallery" },
  { id: "contact", key: "contact" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 border border-[#D4AF37] flex items-center justify-center font-serif-bodoni text-[#D4AF37] text-lg group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
            P
          </span>
          <div className="text-left leading-tight hidden sm:block">
            <div className="font-serif-display text-xl text-[#FDFBF7] tracking-wide">
              Proscenio
            </div>
            <div className="overline text-[10px] tracking-[0.3em] -mt-0.5">
              Ensemble
            </div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => handleNav(item.id)}
              className="text-sm font-light tracking-wide text-[#A1A1AA] gold-link"
            >
              {t.nav[item.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="hidden sm:flex items-center gap-2 text-xs tracking-[0.2em] uppercase"
            data-testid="language-switcher"
          >
            <button
              data-testid="lang-it"
              onClick={() => setLang("it")}
              className={`transition-colors ${
                lang === "it" ? "text-[#D4AF37]" : "text-[#71717A] hover:text-[#FDFBF7]"
              }`}
            >
              IT
            </button>
            <span className="text-[#3a3a3a]">/</span>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`transition-colors ${
                lang === "en" ? "text-[#D4AF37]" : "text-[#71717A] hover:text-[#FDFBF7]"
              }`}
            >
              EN
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#FDFBF7]"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
        >
          <div className="flex flex-col px-6 py-6 gap-4">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-link-mobile-${item.id}`}
                onClick={() => handleNav(item.id)}
                className="text-left text-base font-serif-display text-[#FDFBF7] py-2 border-b border-white/5"
              >
                {t.nav[item.key]}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-2 text-xs tracking-[0.2em] uppercase">
              <button
                data-testid="lang-mobile-it"
                onClick={() => setLang("it")}
                className={lang === "it" ? "text-[#D4AF37]" : "text-[#71717A]"}
              >
                IT
              </button>
              <span className="text-[#3a3a3a]">/</span>
              <button
                data-testid="lang-mobile-en"
                onClick={() => setLang("en")}
                className={lang === "en" ? "text-[#D4AF37]" : "text-[#71717A]"}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
